import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {SampleDetails} from 'constants/SampleConstants';
import {isSchemaTag, SchemaTag, Tag, TargetFile, TargetStorable} from 'folder-items/FileConstants';
import notifications from 'notifications';
import {FieldType, RequiredLinkStorableSchema} from 'schemas/fields/SchemaFieldConstants';
import APIUtil from 'util/APIUtil';

import {SampleFilters, SimpleSampleFilters} from '../SampleFilter';

/**
 * Utilities for using the AddSamples modal in schema tags
 */

export function addSamplesToTag(
  samples: Array<SampleDetails>,
  tag: SchemaTag,
  fieldType: FieldType
): SchemaTag {
  if (!tag.tagSchemaField.isMulti && samples.length > 1) {
    notifications.error('Cannot add multiple samples to field with isMulti false.');
    return tag;
  }
  if (fieldType === FieldType.BIOENTITY_LINK) {
    return addBioentitySamples(samples, tag);
  } else if (fieldType === FieldType.STORABLE_LINK) {
    return addStorableSamples(samples, tag);
  }
  return tag;
}

function addBioentitySamples(samples: Array<SampleDetails>, tag: SchemaTag): SchemaTag {
  const newBioentities: Array<TargetFile> = [];
  for (const sample of samples) {
    if (sample.bioentityInfo != null) {
      // The AddSamples modal returns an object without the URL, which is needed here
      newBioentities.push({...sample.bioentityInfo, url: ''});
    }
  }
  return {
    ...tag,
    // TODO: Figure out if this use of ! is safe.
    targetFiles: [...tag.targetFiles!, ...newBioentities],
  };
}

function addStorableSamples(samples: Array<SampleDetails>, tag: SchemaTag): SchemaTag {
  const newStorables: Array<TargetStorable> = [];
  for (const sample of samples) {
    if (sample.plateInfo != null) {
      newStorables.push({...sample.plateInfo, iconId: ''});
    } else if (sample.boxInfo != null) {
      newStorables.push({...sample.boxInfo, iconId: ''});
    } else if (sample.containerInfo != null) {
      newStorables.push({...sample.containerInfo, iconId: ''});
    } else {
      // NOOP. Ignore samples that are not storables.
    }
  }
  return {
    ...tag,
    // TODO: Figure out if this use of ! is safe.
    targetStorables: [...tag.targetStorables!, ...newStorables],
  };
}

function getLinkedSchemaType(tag: SchemaTag): string | null {
  const requiredLink = tag.tagSchemaField.requiredLink as RequiredLinkStorableSchema;
  const schemaId = requiredLink && requiredLink.storableSchema ? requiredLink.storableSchema.id : null;
  if (schemaId == null) {
    return null;
  }
  return APIUtil.apiIdToType(schemaId);
}

export function canAddSamplesFromModalToTag(tag: Tag, fieldType: FieldType): boolean {
  return (
    isSchemaTag(tag) && (fieldType === FieldType.BIOENTITY_LINK || fieldType === FieldType.STORABLE_LINK)
  );
}

// When adding samples to a field, we can inspect the field type to know what kind of samples are relevant
export function getSampleSourceFilterForField(tag: SchemaTag, fieldType: FieldType): SampleFilters {
  if (fieldType === FieldType.BIOENTITY_LINK) {
    return [SimpleSampleFilters.BIOENTITY];
  }

  assert(
    fieldType === FieldType.STORABLE_LINK,
    'Cannot add samples to a field which is not bioentity or storable',
    {tag}
  );

  const linkedSchemaType = getLinkedSchemaType(tag);
  switch (linkedSchemaType) {
    case ModelTypeConstants.BOX_SCHEMA:
      return [SimpleSampleFilters.BOX];
    case ModelTypeConstants.CONTAINER_SCHEMA:
      return [SimpleSampleFilters.CONTAINER];
    case ModelTypeConstants.PLATE_SCHEMA:
      return [SimpleSampleFilters.PLATE];
    default:
      return [SimpleSampleFilters.BOX, SimpleSampleFilters.CONTAINER, SimpleSampleFilters.PLATE];
  }
}

// Returns the string we should use for both the menu item and the title of the modal
export function addSamplesTitleForField(tag: SchemaTag, fieldType: FieldType, source: string): string {
  assert(
    fieldType === FieldType.BIOENTITY_LINK || fieldType === FieldType.STORABLE_LINK,
    'Add samples modal can only be used on bioentity and storable fields'
  );
  if (fieldType === FieldType.BIOENTITY_LINK) {
    return `Add samples from ${source}`;
  }
  const linkedSchemaType = getLinkedSchemaType(tag);
  if (linkedSchemaType === ModelTypeConstants.PLATE_SCHEMA) {
    return `Add plates from ${source}`;
  } else if (linkedSchemaType === ModelTypeConstants.CONTAINER_SCHEMA) {
    return `Add containers from ${source}`;
  } else {
    return `Add from ${source}`;
  }
}
